import React from 'react'
import GanttChart, { GanttStage } from './GanttChart'

interface TimelineManagerProps {
  acquisitionPlan?: {
    start_date?: string
  }
  selectionStages: GanttStage[]
  onStageUpdate: (stageId: number, updates: Partial<GanttStage>) => Promise<void>
  onStartDateChange: (date: string) => void
}

const TimelineManager: React.FC<TimelineManagerProps> = ({
  acquisitionPlan,
  selectionStages,
  onStageUpdate,
  onStartDateChange
}) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-6">
        Selection Timeline
      </h2>
      
      {/* Start Date */}
      {acquisitionPlan && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Planned Start Date (RFP Release)
          </label>
          <input
            type="date"
            value={acquisitionPlan.start_date || ''}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-50"
          />
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">
            This is the target date for releasing the RFP to vendors. All selection stages will be calculated from this date.
          </p>
        </div>
      )}
      
      {selectionStages.length > 0 ? (
        <div className="space-y-6">
          {/* Gantt Chart */}
          <GanttChart
            stages={selectionStages}
            onStageUpdate={onStageUpdate}
            editable={true}
            rfpReleaseDate={new Date()}
          />

          {/* Detailed Stage List */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
              Stage Details
            </h3>
            <div className="space-y-3">
              {selectionStages.map((stage) => (
                <div 
                  key={stage.id}
                  className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center font-semibold text-sm">
                    {stage.order}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="font-semibold text-slate-900 dark:text-slate-50">
                        {stage.name}
                      </h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        stage.status === 'completed'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : stage.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                          : stage.status === 'delayed'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                          : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {stage.status_display}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      {stage.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-500">
                      <span>Duration: {stage.duration_days} days</span>
                      {stage.start_offset_days !== 0 && (
                        <span>Offset: {stage.start_offset_days > 0 ? '+' : ''}{stage.start_offset_days} days</span>
                      )}
                      {stage.planned_start_date && (
                        <span>Starts: {new Date(stage.planned_start_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <svg className="w-16 h-16 text-slate-400 dark:text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-slate-500 dark:text-slate-500 italic">
            No selection stages have been configured yet
          </p>
        </div>
      )}
    </div>
  )
}

export default TimelineManager

